<script>
  // @ts-check
  import { getTheme } from "../../utilities/themes";
  // eslint-disable-next-line no-unused-vars
  import * as Types from "./globalNavigationTypes";
  import { trapFocus } from "../../utilities/accessibility";

  import GlobalNavigationContent from "./GlobalNavigationDesktopContent.svelte";
  import GlobalNavigationBar from "./GlobalNavigationDesktopBar.svelte";
  import GlobalNavigationAnimationWrapper from "./GlobalNavigationAnimationWrapper.svelte";

  /** @type {import('../../utilities/themes.js').ThemeName} */
  export let theme = "white";

  /** @type {string}  Choose which one is the active route*/
  export let currentRoute;

  /** @type {Types.MainNavigation} */
  export let mainNavigation;

  /** @type {boolean}*/
  export let isExpanded;

  /** @type {string} */
  export let mainLogoAccessibilityText;

  /** @type {boolean} */
  export let hasScrolledDown = false;

  /** @type {boolean}*/
  export let isSearchPanelVisible = false;

  /** @type {boolean}*/
  export let delayThemeFadeOnNavBar = false;

  /** @type {boolean}*/
  export let animateNavBarItems = false;

  let displayedSection = undefined;
  let isHoverActive = false;
  let collapseDisplayedSectionOnSearch = false;
  let contentOpen = false;

  $: {
    if (!isHoverActive && !!displayedSection) {
      isHoverActive = true;
    }
    if (isSearchPanelVisible && !!displayedSection) {
      collapseDisplayedSectionOnSearch = true;
      displayedSection = undefined;
    }
    if (!isSearchPanelVisible) {
      collapseDisplayedSectionOnSearch = false;
    }
  }

  /** @type {import('../../utilities/themes').Theme}*/
  $: currentDesktopTheme = getTheme(theme);

  const handleHoveredLabel = (e) => {
    displayedSection = e.detail.displayedSection;

    // if the primary section being hovered does not have any secondary sections, we do not display the drop down secondary menu
    const hoveredSection = mainNavigation.find(
      (section) => section.link.label === displayedSection
    );
    if (hoveredSection.secondarySections.length === 0) {
      handleHideSecondarySection();
      return;
    }
    isExpanded = true;
  };

  const handleHideSecondarySection = () => {
    displayedSection = undefined;
    isHoverActive = false;
    isExpanded = false;
  };

  $: if (!displayedSection && contentOpen) {
    contentOpen = false;
  }

  const handleOnIntroEnd = () => {
    contentOpen = true;
  };

  $: navTransitionDelay = isExpanded || isSearchPanelVisible ? "delay-[2000ms]" : "";
  $: navBarHeightClass = `h-nav-height-desktop ${hasScrolledDown ? "lg:h-[74px] xl:h-[82px]" : ""}`;
</script>

<nav
  data-testId="nav-desktop"
  class="{hasScrolledDown || isExpanded
    ? currentDesktopTheme?.backgroundColor
    : 'bg-transparent'} cta-md sticky left-0 top-0 z-[--stack-level-global-nav] hidden {navBarHeightClass} w-full transition-all {navTransitionDelay} duration-[300ms] ease-nav-easing lg:block"
  class:shadow-xl={hasScrolledDown}
  on:mouseleave={handleHideSecondarySection}
  use:trapFocus={{
    shouldTrapFocus: isSearchPanelVisible,
    triggerElementQuery: ""
  }}
>
  <GlobalNavigationBar
    {mainNavigation}
    {displayedSection}
    {currentRoute}
    {theme}
    {hasScrolledDown}
    on:hoveredLabel={handleHoveredLabel}
    on:searchClose
    {mainLogoAccessibilityText}
    {isSearchPanelVisible}
    {delayThemeFadeOnNavBar}
    {animateNavBarItems}
  >
    <svelte:fragment slot="utilityNavigation">
      <slot name="utilityNavigation" />
    </svelte:fragment>
  </GlobalNavigationBar>
  {#if displayedSection}
    <GlobalNavigationAnimationWrapper
      on:introEnd={handleOnIntroEnd}
      {theme}
      {collapseDisplayedSectionOnSearch}
    >
      <svelte:fragment slot="content">
        <GlobalNavigationContent {mainNavigation} {displayedSection} {theme} {contentOpen} />
      </svelte:fragment>
    </GlobalNavigationAnimationWrapper>
  {/if}
  {#if isSearchPanelVisible}
    <GlobalNavigationAnimationWrapper
      {isSearchPanelVisible}
      {theme}
      {collapseDisplayedSectionOnSearch}
    >
      <svelte:fragment slot="content">
        <slot name="searchPanel" />
      </svelte:fragment>
    </GlobalNavigationAnimationWrapper>
  {/if}
</nav>
